import { Tab, Tabs, TabsList } from "@mui/base";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Icon, TenantAccountAttributes } from "~/api/Api";
import { TeamTenant } from "~/components/settings/TeamTenant";
import { useAuth } from "~/contexts/AuthProvider";
import { useBillingAccess } from "~/contexts/BillingAccessContext.tsx";
import { useTeam } from "~/contexts/TeamProvider";
import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
  useFeatureFlagsConfig,
} from "~/featureFlags";
import { canMember } from "~/helpers/authorizations";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch.ts";
import { TenantData } from "~/pages/settings/Settings";

import { TeamAttributes, UserAttributes } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import { uploadAndGetSignedId, uploadFile } from "../../api/apiFunctions";
import Button from "../mantine/button/Button";
import Spinner from "../ui/spinner/Spinner";
import AccountSecurity from "./AccountSecurity";
import styles from "./Settings.module.scss";
import TeamAIEngine from "./TeamAIEngine";
import { TeamCrawlerSettings } from "./TeamCrawlerSettings";
import TeamDetails from "./TeamDetails";
import ThemeToggle from "./ThemeToggle";

export type SettingsMenuItem = {
  id: string;
  value: number;
  label: string;
  title: string;
  link?: string;
  section?: string;
  feature?: string;
  content: () => React.ReactNode;
  subMenu?: never;
};

interface FormValues {
  // user
  first_name?: string;
  last_name?: string;
  email?: string;
  // team
  name?: string;
  custom_bot_name?: string;
  logo?: string | null | Icon;
  logoFile?: File | null;
  ai_title?: boolean;
  ai_description?: boolean;
  ai_meta_override?: number;
  ai_schema?: boolean;
  ai_schema_override?: number;
  // tenant account
  wl_name?: string;
  wl_address?: string;
  wl_support_email?: string;
  wl_logo?: string | null | Icon; // signed_id
  wl_logoFile?: File | null;
}

const Settings = ({ tenantAccountData }: { tenantAccountData: TenantData }) => {
  const {
    user,
    refetchUser,
    isFetching: isFetchingUser,
    authorizations: userAuthorizations,
  } = useAuth();
  const {
    team,
    refetchTeam,
    isFetching: isFetchingTeam,
    authorizations: teamAuthorizations,
  } = useTeam();
  const { subscription } = useBillingAccess();

  const canUpdateTeam = canMember(teamAuthorizations, "update");
  const canUpdateUser = canMember(userAuthorizations, "update");

  const location = useLocation();
  const navigate = useNavigate();
  const { tabId } = useParams();
  const queryClient = useQueryClient();
  const flags = useFeatureFlagsConfig();

  const [formDefaultValues, setFormDefaultValues] = useState<FormValues>({});

  // --- Tenant auth/flags ---
  const [isTenantAccountLogoUploading, setIsTenantAccountLogoUploading] =
    useState(false);
  const wlAccount = tenantAccountData?.account ?? null;
  const wlAuth = tenantAccountData?.authorizations;
  const canCreateTenantAccount: boolean = Boolean(
    wlAuth?.collection?.includes("create"),
  );
  const canUpdateTenantAccount: boolean = canMember(wlAuth, "update");
  const isWLAllowed = Boolean(canUpdateTenantAccount || canCreateTenantAccount);

  const isCrawlerSettingsAllowed =
    canUpdateTeam && !!subscription?.price?.product?.white_label;

  const methods = useForm<FormValues>({
    defaultValues: formDefaultValues,
    mode: "onSubmit",
  });

  const {
    handleSubmit,
    formState: { isDirty, dirtyFields },
    reset,
    setValue,
  } = methods;

  // ---- defaults ----
  useEffect(() => {
    if (user && team) {
      const userData = {
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
      };
      const teamData = {
        name: team.name || "",
        logo: team.logo || "",
        ai_title: team.ai_title || false,
        ai_description: team.ai_description || false,
        ai_meta_override: team.ai_meta_override || 0,
        ai_schema: team.ai_schema || false,
        ai_schema_override: team.ai_schema_override || 0,
        logoFile: null,
      };
      const teamCrawlerData = {
        custom_bot_name: team.custom_bot_name || "",
      };
      const wlData = {
        wl_name: wlAccount?.name || "",
        wl_support_email: wlAccount?.support_email || "",
        wl_address: wlAccount?.address || "",
        wl_logo: wlAccount?.logo || null,
        wl_logoFile: null,
      };
      const combinedValues = {
        ...userData,
        ...teamData,
        ...teamCrawlerData,
        ...wlData,
      };
      setFormDefaultValues(combinedValues);
      reset(combinedValues);
    }
  }, [user, team, wlAccount, reset]);

  // -------- Uploads --------
  const { mutate: mutateTeamLogo, isPending: isTeamLogoUploading } =
    useMutation({
      mutationFn: ({
        file,
        collection,
        reflection,
      }: {
        file: File;
        collection?: string;
        reflection?: string;
      }) => uploadFile(file, collection, reflection),
      onError: () => {
        enqueueSnackbar("Error uploading file!", {
          variant: "error",
          persist: true,
        });
      },
      onSuccess: async (response, { file }) => {
        enqueueSnackbar("File uploaded successfully!", { variant: "success" });

        const data =
          typeof response.json === "function"
            ? await response.json()
            : response;
        const { direct_upload } = data;
        if (!direct_upload?.url) return;

        try {
          await fetch(direct_upload.url, {
            method: "PUT",
            headers: direct_upload.headers,
            body: file,
          });
          //must be after file upload to S3
          updateTeam({
            logo: data.signed_id,
          });
        } catch {
          enqueueSnackbar("Error uploading file", {
            variant: "error",
            persist: true,
          });
        }
      },
    });

  // -------- Mutations: user/team --------
  const { mutate: updateUser, isPending: userUpdatePending } = useMutation({
    mutationFn: async (data: UserAttributes) => {
      const response = await apiClient.user.userPartialUpdate(data);

      return response.data;
    },
    onSuccess: async () => {
      enqueueSnackbar("User settings updated successfully", {
        variant: "success",
      });

      await refetchUser();
    },
    onError: (error) => {
      // @ts-expect-error BE types
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
  });

  const { mutate: updateTeam, isPending: teamUpdatePending } = useMutation({
    mutationFn: async (data: TeamAttributes) => {
      const response = await apiClient.teams.teamsPartialUpdate(
        team?.id ?? 0,
        data,
      );

      return response.data;
    },
    onSuccess: async () => {
      enqueueSnackbar("Team settings updated successfully", {
        variant: "success",
      });

      await refetchTeam();
      await queryClient.refetchQueries({ queryKey: ["teams"] });
    },
    onError: (error) => {
      // @ts-expect-error BE types
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
  });

  // -------- Mutations: tenant --------
  const { mutate: createTenantAccount, isPending: wlCreatePending } =
    useMutation({
      mutationFn: async (payload: TenantAccountAttributes) => {
        const result = await tryCatch(apiClient.tenant.accountCreate(payload));

        if (result.error) {
          enqueueSnackbar(messageFromError(result.error), {
            variant: "error",
            persist: true,
          });

          throw result.error;
        }

        return result.data;
      },
      onSuccess: () => {
        enqueueSnackbar("Account created", { variant: "success" });
        queryClient.invalidateQueries({
          queryKey: ["tenantAccount", team?.id],
        });
      },
    });

  const { mutate: updateTenantAccount, isPending: wlUpdatePending } =
    useMutation({
      mutationFn: async (payload: TenantAccountAttributes) => {
        const result = await tryCatch(
          apiClient.tenant.accountPartialUpdate(payload),
        );

        if (result.error) {
          enqueueSnackbar(messageFromError(result.error), {
            variant: "error",
            persist: true,
          });

          throw result.error;
        }

        return result.data;
      },
      onSuccess: () => {
        enqueueSnackbar("Account updated", { variant: "success" });
        queryClient.invalidateQueries({
          queryKey: ["tenantAccount", team?.id],
        });
      },
    });

  const handleSave = handleSubmit(async (data) => {
    // uploads
    if (dirtyFields.logoFile && data.logoFile) {
      mutateTeamLogo({
        file: data.logoFile,
        collection: "teams",
        reflection: "logo",
      });
    }

    const userFieldsChanged = ["first_name", "last_name", "email"].some(
      (field) => dirtyFields[field as keyof FormValues],
    );

    const teamCrawlerFieldsChanged = ["custom_bot_name"].some(
      (field) => dirtyFields[field as keyof FormValues],
    );

    const teamFieldsChanged = [
      "name",
      "ai_title",
      "ai_description",
      "ai_meta_override",
      "ai_schema",
      "ai_schema_override",
    ].some((field) => dirtyFields[field as keyof FormValues]);

    const updatePromises: Promise<void>[] = [];

    if (userFieldsChanged) {
      const userData: UserAttributes = {
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        email: data.email || "",
      };

      updatePromises.push(
        new Promise((resolve) =>
          updateUser(userData, { onSettled: () => resolve() }),
        ),
      );
    }

    if (teamFieldsChanged || dirtyFields.logo) {
      const teamData = {
        name: data.name,
        ai_title: data.ai_title,
        ai_description: data.ai_description,
        ai_meta_override: data.ai_meta_override,
        ai_schema: data.ai_schema,
        ai_schema_override: data.ai_schema_override,
        ...(dirtyFields.logo && { logo: null }),
      } as TeamAttributes;

      updatePromises.push(
        new Promise((resolve) =>
          updateTeam(teamData, { onSettled: () => resolve() }),
        ),
      );
    }

    if (teamCrawlerFieldsChanged) {
      const teamCrawlerData = {
        custom_bot_name: data.custom_bot_name,
      } as TeamAttributes;

      updatePromises.push(
        new Promise((resolve) =>
          updateTeam(teamCrawlerData, { onSettled: () => resolve() }),
        ),
      );
    }

    // tenant

    const wlPayload: TenantAccountAttributes = {};

    if (dirtyFields.wl_name) wlPayload.name = data.wl_name?.trim();
    if (dirtyFields.wl_address)
      wlPayload.address = data.wl_address?.trim() ?? null;
    if (dirtyFields.wl_support_email)
      wlPayload.support_email = data.wl_support_email?.trim() ?? null;

    let shouldSendWL = Object.keys(wlPayload).length > 0;

    if (dirtyFields.wl_logoFile && data.wl_logoFile) {
      try {
        setIsTenantAccountLogoUploading(true);
        const signedId = await uploadAndGetSignedId(
          data.wl_logoFile,
          "tenant/accounts",
          "logo",
        );
        wlPayload.logo = signedId;
        shouldSendWL = true;

        setValue("wl_logo", signedId, { shouldDirty: true });
      } catch {
        enqueueSnackbar("Error uploading tenant logo", {
          variant: "error",
          persist: true,
        });
        // return; if we want to interrupt submit
      } finally {
        setIsTenantAccountLogoUploading(false);
      }
    }

    const currentWlLogo = methods.getValues("wl_logo");
    const previousWlLogo = formDefaultValues.wl_logo;

    // was deleted
    if (
      typeof wlPayload.logo === "undefined" &&
      ((dirtyFields.wl_logo && currentWlLogo === null) ||
        (typeof previousWlLogo !== "undefined" &&
          previousWlLogo !== null &&
          currentWlLogo === null))
    ) {
      wlPayload.logo = null;
      shouldSendWL = true;
    }

    if (shouldSendWL) {
      updatePromises.push(
        new Promise((resolve) => {
          if (wlAccount) {
            updateTenantAccount(wlPayload, { onSettled: () => resolve() });
          } else if (canCreateTenantAccount) {
            createTenantAccount(wlPayload, { onSettled: () => resolve() });
          } else {
            resolve();
          }
        }),
      );
    }

    if (updatePromises.length > 0) {
      await Promise.all(updatePromises);

      // update form default values after form submit
      const current = methods.getValues();
      setFormDefaultValues(current);
      methods.reset(current);
    }
  });

  const handleRevert = () => {
    methods.reset(formDefaultValues);
  };

  const isPending =
    userUpdatePending ||
    teamUpdatePending ||
    isTeamLogoUploading ||
    wlCreatePending ||
    wlUpdatePending ||
    isTenantAccountLogoUploading;

  const tabs = useMemo((): SettingsMenuItem[] => {
    const base = [
      {
        id: "account",
        value: 0,
        label: "Account & Security",
        title: "Account & Security Settings",
        section: "account",
        feature: FEATURE_FLAGS.SETTINGS_ACCOUNT,
        content: () => <AccountSecurity />,
      },
      {
        id: "theme",
        value: 1,
        label: "Theme",
        title: "Theme Settings",
        feature: FEATURE_FLAGS.SETTINGS_THEME,
        content: () => <ThemeToggle />,
        section: "account",
      },
      {
        id: "team",
        value: 2,
        label: "Team Details",
        title: "Team Details",
        feature: FEATURE_FLAGS.SETTINGS_TEAM,
        content: () => <TeamDetails />,
        section: "team",
      },
    ];

    if (team?.ai_engine_available) {
      base.push({
        id: "ai",
        value: 3,
        label: "AI Engine",
        title: "Team AI Engine Settings",
        feature: FEATURE_FLAGS.SETTINGS_AI,
        content: () => <TeamAIEngine />,
        section: "team",
      });
    }

    if (isCrawlerSettingsAllowed) {
      base.push({
        id: "crawler",
        value: 4,
        label: "Crawler",
        title: "Team Crawler Settings",
        feature: FEATURE_FLAGS.SETTINGS_CRAWLER,
        content: () => <TeamCrawlerSettings />,
        section: "team",
      });
    }

    if (isWLAllowed) {
      base.push({
        id: "white-label",
        value: 5,
        label: "White-Label",
        title: "White-Label Settings",
        section: "team",
        feature: FEATURE_FLAGS.SETTINGS_TENANT,
        content: () => (
          <TeamTenant
            account={wlAccount}
            canCreate={canCreateTenantAccount}
            canUpdate={canUpdateTenantAccount}
          />
        ),
      });
    }

    if (!flags) return base;

    return base.filter(
      (item) => !item.feature || isFeatureFlagEnabled(flags, item.feature),
    );
  }, [
    isCrawlerSettingsAllowed,
    isWLAllowed,
    wlAccount,
    canCreateTenantAccount,
    canUpdateTenantAccount,
    updateUser,
    flags,
  ]);

  // -------- Tabs --------
  const currentPath = location.pathname;
  const currentTabId = tabId || currentPath.split("/").pop();
  const activeTab = (() => {
    if (currentPath === "/settings" || currentPath === "/settings/") {
      return tabs[0]?.value ?? 0;
    }

    return (
      tabs.find((tab) => tab.id === currentTabId)?.value ||
      (tabs[0]?.value ?? 0)
    );
  })();
  const currentTab = (() => {
    if (currentPath === "/settings" || currentPath === "/settings/") {
      return tabs[0];
    }

    return tabs.find((tab) => tab.id === currentTabId) || tabs[0];
  })();

  const handleTabChange = (
    _event: React.SyntheticEvent | null,
    newValue: string | number | null,
  ) => {
    const selectedTab = tabs.find((tab) => tab.value === newValue);
    if (selectedTab) {
      if (selectedTab.id === "account") {
        navigate("/settings");
      } else {
        navigate(`/settings/${selectedTab.id}`);
      }
    }
  };

  /**
   * Determine edit permissions based on the currently active tab.
   *
   * Settings has two logical permission domains:
   *  - "account"  → user-level permissions (profile, email, password, theme)
   *  - "team"     → team-level permissions (team details, AI, crawler, white-label)
   *
   * Since the form is shared across tabs but only one tab is active at a time,
   * we gate the Save button based on the active section instead of analyzing
   * dirty fields per domain.
   *
   * This keeps the logic simple and predictable:
   *  - Account tab → requires canUpdateUser
   *  - All other tabs → require canUpdateTeam
   */
  const canEditCurrentSection =
    currentTab?.section === "account" ? canUpdateUser : canUpdateTeam;

  const disabled =
    !isDirty ||
    isPending ||
    isFetchingTeam ||
    isFetchingUser ||
    !canEditCurrentSection;

  const teamTabs = tabs.filter((tab) => tab.section === "team");
  const accountTabs = tabs.filter((tab) => tab.section === "account");

  return (
    <main className={styles.settings}>
      <FormProvider {...methods}>
        <form onSubmit={handleSave}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {/*
             // @ts-expect-error TS2551 non-existing className */}
            <div className={styles.settings__content}>
              {/*
               // @ts-expect-error TS2551 non-existing className */}
              <nav className={styles.settings__tabs}>
                {accountTabs.length > 0 ? (
                  // @ts-expect-error TS2551 non-existing className
                  <div className={styles.settings__tabSection}>
                    <h3 className={styles.settingsTabSectionTitle}>Account</h3>
                    <TabsList className={styles.settingsTabsList}>
                      {accountTabs.map((tab) => (
                        <Tab
                          key={tab.id}
                          // @ts-expect-error TS2551 non-existing className
                          className={styles.settings__tab}
                          value={tab.value}
                        >
                          {tab.label}
                        </Tab>
                      ))}
                    </TabsList>
                  </div>
                ) : null}
                {teamTabs.length > 0 ? (
                  // @ts-expect-error TS2551 non-existing className
                  <div className={styles.settings__tabSection}>
                    <h3 className={styles.settingsTabSectionTeamTitle}>Team</h3>
                    <TabsList className={styles.settingsTabsList}>
                      {teamTabs.map((tab) => (
                        <Tab
                          key={tab.id}
                          // @ts-expect-error TS2551 non-existing className
                          className={styles.settings__tab}
                          value={tab.value}
                        >
                          {tab.label}
                        </Tab>
                      ))}
                    </TabsList>
                  </div>
                ) : null}
              </nav>
              {currentTab ? (
                // @ts-expect-error TS2551 non-existing className
                <div className={styles.settings__panel}>
                  {/*
                   // @ts-expect-error TS2551 non-existing className */}
                  <section className={styles.settingsSection}>
                    <h2 className={styles.settingsSectionTitle}>
                      {currentTab.title}
                    </h2>

                    {currentTab.content()}
                  </section>
                </div>
              ) : null}
            </div>
          </Tabs>
          <div className={styles.settingsFooter}>
            <div className={styles.settingsFooterButtons}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleRevert}
                disabled={disabled}
              >
                Revert
              </Button>
              <Button type="submit" disabled={disabled}>
                {isPending && <Spinner size="1rem" />}Save Changes
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </main>
  );
};

export default Settings;
